import * as React from "react";

import { assertNever } from "@impero-com/common/utils/obj";

import {
    TextAreaInput,
    TextAreaProps,
} from "components/form/legacyFields/textAreaInput";
import * as CT from "org/risks/content/types";

import { mapForm, simpleMappers, WrappedFormUtils } from "../../../utils/form";
import * as U from "../content/utils";

import * as T from "./types";

type TextAreaInputMetadataFieldProps<ROOT> = T.SharedProperties<
    string,
    ROOT,
    CT.TextAreaInstance
>;

export default class TextAreaMetadataField<ROOT> extends React.PureComponent<
    TextAreaInputMetadataFieldProps<ROOT>
> {
    render = () => {
        const { id, label, instance } = this.props;
        const baseProps = {
            id,
            label,
        };
        let props: TextAreaProps<string, ROOT>;
        switch (this.props.type) {
            case "CREATE":
                props = {
                    ...baseProps,
                    type: this.props.type,
                    initialValue: undefined,
                    form: mapInstanceFormToStringForm(
                        id,
                        this.props.form,
                        instance
                    ),
                };
                break;
            case "EDIT":
                props = {
                    ...baseProps,
                    type: this.props.type,
                    value: this.props.instance.value,
                    onChange: this.handleOnChange,
                    validationError: this.props.validationError,
                };
                break;
            case "READ_ONLY":
                props = {
                    ...baseProps,
                    type: this.props.type,
                    value: this.props.instance.value,
                };
                break;
            default:
                return assertNever(this.props);
        }
        return <TextAreaInput {...props} />;
    };

    private handleOnChange = (value: string | undefined) => {
        const { instance } = this.props;
        if (this.props.type === "EDIT") {
            return this.props.onChangeAsync(
                Promise.resolve(value),
                (_prev, val) =>
                    U.extractMetadataInstanceValue(
                        stringToInstance(val, instance)
                    )
            );
        }
        return Promise.reject(
            new Error("Cannot change value in non-edit mode")
        );
    };
}

function instanceToString(instance: CT.MetadataInstance): string | undefined {
    if (instance.type !== "TextAreaInstance") {
        throw new Error("Expected textarea instance, got " + instance.type);
    }
    return instance.value;
}

function stringToInstance(
    stringValue: string | undefined,
    oldInstance: CT.MetadataInstance
): CT.MetadataInstance {
    if (oldInstance.type !== "TextAreaInstance") {
        throw new Error("Expected textarea instance, got " + oldInstance.type);
    }
    return {
        ...oldInstance,
        value: stringValue !== "" ? stringValue : undefined,
    };
}

function mapInstanceFormToStringForm<ROOT>(
    id: string,
    instanceForm: WrappedFormUtils<
        {
            [x: string]: CT.MetadataInstance;
        },
        ROOT
    >,
    instance: CT.MetadataInstance
): WrappedFormUtils<
    {
        [x: string]: string;
    },
    ROOT
> {
    const mapper = simpleMappers<
        string,
        CT.MetadataInstance,
        string | undefined,
        ROOT
    >(id, instanceToString, (value) => stringToInstance(value, instance));
    return mapForm<Record<string, string>>()(instanceForm, mapper());
}
